import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useAuth } from "../../../login/AuthProvider.js";
import "./EditPermission.css";

function EditPermission() {
  const { userId } = useParams();
  const { user: authUser } = useAuth();

  const [pages, setPages] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [targetUserRole, setTargetUserRole] = useState("");
  const [targetUser, setTargetUser] = useState(null);

  // Pricing access state (user-level flag)
  const [hasPricingAccess, setHasPricingAccess] = useState(false);

  const currentUser = authUser;
  const currentUserId = currentUser?.userId;
  const currentUserRole = currentUser?.role;

  const urlUserId = parseInt(userId, 10);

  const isViewingOwnAccount = currentUserId === urlUserId;
  const isAdmin = currentUserRole === "Admin";

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const fetchPages = fetch(
      "https://hms.backend.hiragroupcloud.com/api/page/pages"
    ).then((r) => r.json());

    const fetchPerms = fetch(
      `https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/pages`
    ).then((r) => r.json());

    const fetchUserData = fetch(
      `https://hms.backend.hiragroupcloud.com/api/auth/users/${userId}`
    ).then((r) => r.json());

    const fetchPricingAccess = fetch(
      `https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/pricing-access`
    ).then((r) => r.json());

    Promise.all([fetchPages, fetchPerms, fetchUserData, fetchPricingAccess])
      .then(([pagesData, permsData, userData, pricingData]) => {
        if (!mounted) return;

        setPages(Array.isArray(pagesData) ? pagesData : []);

        if (userData && userData.user) {
          setTargetUser(userData.user);
          setTargetUserRole(userData.user.role || "User");
        }

        const permissionMap = Array.isArray(permsData)
          ? permsData.reduce((acc, perm) => {
              if (perm && perm.page && perm.page._id) {
                acc[perm.page._id] = !!perm.isVisible;
              }
              return acc;
            }, {})
          : {};

        setPermissions(permissionMap);

        if (pricingData && typeof pricingData.hasPricingAccess === "boolean") {
          setHasPricingAccess(pricingData.hasPricingAccess);
        }
      })
      .catch((err) => {
        console.error("Error fetching pages or permissions:", err);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [userId]);

  const handlePermissionChange = (pageId, isVisible) => {
    if (!isAdmin || targetUserRole === "Admin" || isViewingOwnAccount) {
      return;
    }

    setPermissions((prev) => ({
      ...prev,
      [pageId]: isVisible,
    }));
  };

  const handlePricingAccessChange = (checked) => {
    if (!isAdmin || targetUserRole === "Admin" || isViewingOwnAccount) {
      return;
    }

    setHasPricingAccess(checked);
  };

  const handleSubmit = () => {
    if (!isAdmin) {
      alert("Only Admin users can modify permissions");
      return;
    }

    if (isViewingOwnAccount) {
      alert(
        "You cannot modify your own permissions from this page. Please contact another administrator."
      );
      return;
    }

    if (targetUserRole === "Admin") {
      alert("Cannot modify permissions of Admin users");
      return;
    }

    const updatedPermissions = Object.keys(permissions).map((pageId) => ({
      pageId,
      isVisible: !!permissions[pageId],
    }));

 

    fetch(`https://hms.backend.hiragroupcloud.com/api/page/user/${userId}/permissions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        permissions: updatedPermissions,
        requestingUserId: currentUserId,
        requestingUserRole: currentUserRole,
        hasPricingAccess,
      }),
    })
      .then(async (res) => {
        const data = await res.json();

        if (!res.ok) {
          throw new Error(
            data.message || `HTTP error! status: ${res.status}`
          );
        }

        return data;
      })
      .then((data) => {
        alert(data.message || "Permissions updated successfully");
      })
      .catch((err) => {
        console.error("Error updating permissions:", err);
        alert(`Failed to update permissions: ${err.message}`);
      });
  };

  if (loading) {
    return <div className="permission-loading">Loading...</div>;
  }

  if (!isAdmin) {
    return (
      <div className="permission-access-denied">
        <h2>Access Denied</h2>
        <p>Only Admin users can edit permissions</p>
      </div>
    );
  }

  const groupedPages = pages.reduce((acc, page) => {
    const cat = page.category || "Other";

    if (!acc[cat]) {
      acc[cat] = [];
    }

    acc[cat].push(page);

    return acc;
  }, {});

  const shouldDisable =
    !isAdmin || targetUserRole === "Admin" || isViewingOwnAccount;

  return (
    <>
      <div className="permission-page">
        <div className="permission-header">
          <h1 className="permission-title">Edit User Permissions</h1>

          {targetUser && (
            <div className="editing-user">
              <strong>Editing:</strong> {targetUser.first_name}{" "}
              {targetUser.last_name}
              <span
                className="user-role"
                style={{
                  backgroundColor:
                    targetUserRole === "Admin" ? "#dc3545" : "#004aad",
                }}
              >
                {targetUserRole}
              </span>
            </div>
          )}
        </div>

        <div className="permission-container">
          {shouldDisable && (
            <div className="permission-warning">
              {isViewingOwnAccount
                ? "⚠️ You cannot modify your own permissions from this page."
                : targetUserRole === "Admin"
                ? "⚠️ Cannot modify permissions of Admin users."
                : "⚠️ You do not have permission to modify permissions."}
            </div>
          )}

          {/* ============================================
              PRICING TAB (user-level flag)
          ============================================ */}
          <div className="permission-category pricing-tab">
            <div className="category-title">Pricing</div>

            <div className="permission-grid">
              <div className="permission-item">
                <input
                  type="checkbox"
                  id="pricing-access-toggle"
                  checked={hasPricingAccess}
                  disabled={shouldDisable}
                  onChange={(e) => handlePricingAccessChange(e.target.checked)}
                  className="permission-checkbox"
                />
                <label
                  htmlFor="pricing-access-toggle"
                  className="permission-label"
                  style={{
                    cursor: shouldDisable ? "not-allowed" : "pointer",
                    color: shouldDisable ? "#666" : "#333",
                  }}
                >
                  Enable Pricing Access
                </label>
              </div>
            </div>

            {!hasPricingAccess && (
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "12px",
                  color: "#b45309",
                  fontWeight: 500,
                }}
              >
                ⚠️ When disabled, this user will not see any amount / price
                values across the app.
              </div>
            )}
          </div>
          {/* ============================================ */}

          {Object.keys(groupedPages).map((category) => (
            <div key={category} className="permission-category">
              <div className="category-title">{category}</div>

              <div className="permission-grid">
                {groupedPages[category].map((page) => {
                  const checked = shouldDisable
                    ? true
                    : !!permissions[page._id];

                  return (
                    <div key={page._id} className="permission-item">
                      <input
                        type="checkbox"
                        id={`page-${page._id}`}
                        checked={checked}
                        disabled={shouldDisable}
                        onChange={(e) =>
                          handlePermissionChange(page._id, e.target.checked)
                        }
                        className="permission-checkbox"
                      />

                      <label
                        htmlFor={`page-${page._id}`}
                        className="permission-label"
                        style={{
                          cursor: shouldDisable ? "not-allowed" : "pointer",
                          color: shouldDisable ? "#666" : "#333",
                        }}
                      >
                        {page.name}
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          <button
            onClick={handleSubmit}
            disabled={shouldDisable}
            className="save-permission-button"
            onMouseEnter={(e) => {
              if (!shouldDisable) {
                e.target.style.backgroundColor = "#003580";
              }
            }}
            onMouseLeave={(e) => {
              if (!shouldDisable) {
                e.target.style.backgroundColor = "#004aad";
              }
            }}
          >
            {shouldDisable ? "Permissions Locked" : "Save Permissions"}
          </button>
        </div>
      </div>
    </>
  );
}

export default EditPermission;